/**
 * ExportCompleteDialog — the PRE-download review dialog (B-7202, was B-932's
 * post-download summary).
 *
 * Founder, 2026-09-30: show it when the user clicks to export, not after. It
 * leads with how many lines need review, because checking them in our Review
 * section before downloading is easier than finding them in Xactimate. Below
 * that sit the server-owned steps (same text as the summary email) and every
 * line to check, with notes already in plain wording.
 *
 * Three ways out:
 *  - Download: the page exports; the "Don't show this again" box decides
 *    whether it then emails the summary (unticked) or saves the opt-out (ticked).
 *  - Go to review: no download, no email; the page opens its Review view.
 *  - Cancel (also X, Escape, backdrop): nothing happens.
 */
import { useState } from 'react';

import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { XACTIMATE_PRICE_LIST_NOTICE } from '../lib/notices';
import type { ExportReviewNote } from '../../types';
import type { ExportReviewEntry, PasteCheck } from '../../types/exportSummary';

/** Props for {@link ExportCompleteDialog}. */
export interface ExportCompleteDialogProps {
  /** Lines flagged for review (server count); undefined while loading. */
  reviewCount?: number;
  /** Every line to check, notes in plain wording; undefined while loading. */
  reviewNotes?: ExportReviewNote[];
  /** The same lines with repeats collapsed (preferred when present). */
  reviewEntries?: ExportReviewEntry[];
  /** Lines whose formula may not paste into Xactimate (PRP-MCP-8 G8). */
  pasteCheck?: PasteCheck | null;
  /** Numbered next steps from the server; undefined while loading. */
  steps?: string[];
  /** True while the summary is being computed on the server. */
  loading: boolean;
  /** Export now; `dontShowAgain` is the checkbox state. */
  onDownload: (_dontShowAgain: boolean) => void;
  /** Open the Review view instead of downloading. */
  onGoToReview: () => void;
  /** Close without doing anything. */
  onCancel: () => void;
}

const H3 = 'text-[11px] uppercase tracking-[.08em] text-[var(--tx-lo)] mb-1';

/** Pre-download dialog: review callout, steps, lines to check, three actions. */
export default function ExportCompleteDialog({
  reviewCount,
  reviewNotes,
  reviewEntries,
  pasteCheck,
  steps,
  loading,
  onDownload,
  onGoToReview,
  onCancel,
}: ExportCompleteDialogProps) {
  // Unchecked by default: downloading without ticking it sends the summary email.
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const n = reviewCount ?? reviewNotes?.length ?? 0;

  return (
    <Modal
      label="Before you download"
      title="Before you download"
      subtitle="What to check, and what to do in Xactimate after you open the file."
      onClose={onCancel}
      testId="export-dialog"
      className="max-w-2xl"
      footer={
        <>
          <label className="mr-auto flex items-center gap-2 text-[12px] text-[var(--tx-lo)] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              data-testid="export-dialog-dont-show"
            />
            Don&apos;t show this again
          </label>
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="export-dialog-cancel">
            Cancel
          </Button>
          <Button variant="secondary" size="sm" onClick={onGoToReview} data-testid="export-dialog-review">
            Go to review
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onDownload(dontShowAgain)}
            data-testid="export-dialog-download"
          >
            Download
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {loading ? (
          <p className="text-[13px] text-[var(--tx-lo)]" data-testid="export-dialog-loading">
            Checking this estimate…
          </p>
        ) : (
          <div
            className="rounded-[var(--r-sm)] border border-[var(--bd)] bg-[var(--bg-2)] px-3 py-2"
            data-testid="export-dialog-callout"
          >
            {n > 0 ? (
              <>
                <div className="text-[14px] font-semibold text-[var(--tx-hi)]">
                  {n} {n === 1 ? 'line needs' : 'lines need'} review
                </div>
                <p className="text-[12.5px] text-[var(--tx-lo)] mt-0.5">
                  You can check or change {n === 1 ? 'it' : 'them'} in the Review section before
                  you download. That&apos;s easier than finding {n === 1 ? 'it' : 'them'} in
                  Xactimate.
                </p>
              </>
            ) : (
              <div className="text-[13px] text-[var(--tx-hi)]">
                No lines need review. Every line matched a catalog code outright.
              </div>
            )}
          </div>
        )}

        <p className="text-[13px] text-[var(--tx-hi)]" data-testid="export-price-list-notice">
          {XACTIMATE_PRICE_LIST_NOTICE}
        </p>

        {steps && steps.length > 0 && (
          <section data-testid="export-dialog-steps">
            <h3 className={H3}>After you download, in Xactimate</h3>
            <ol className="list-decimal pl-5 space-y-1.5 text-[13px] text-[var(--tx-hi)]">
              {steps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          </section>
        )}

        {reviewEntries && reviewEntries.length > 0 ? (
          <section data-testid="export-dialog-review-list">
            <h3 className={H3}>Lines to check ({n})</h3>
            {/* Repeats (same code, description, reason) are one entry; counts stay per line. */}
            <ul className="space-y-2">
              {reviewEntries.map((en, i) => (
                <li
                  key={`${en.line_numbers[0] ?? 'x'}-${i}`}
                  className="rounded-[var(--r-sm)] border border-[var(--bd)] bg-[var(--bg-2)] px-3 py-2"
                  data-testid="export-dialog-review-item"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    {en.lines_label && (
                      <span className="font-[var(--font-mono)] text-[11px] text-[var(--tx-lo)]">
                        {en.lines_label}
                      </span>
                    )}
                    <span className="text-[13px] text-[var(--tx-hi)]">{en.description}</span>
                    {en.code && (
                      <span className="font-[var(--font-mono)] text-[11px] text-[var(--tx-lo)]">
                        {en.code}
                      </span>
                    )}
                  </div>
                  {en.rooms_label && (
                    <p className="text-[11px] text-[var(--tx-lo)] mt-0.5">{en.rooms_label}</p>
                  )}
                  <p className="text-[12px] text-[var(--tx-lo)] mt-0.5">{en.note}</p>
                </li>
              ))}
            </ul>
          </section>
        ) : reviewNotes && reviewNotes.length > 0 && (
          <section data-testid="export-dialog-review-list">
            <h3 className={H3}>Lines to check ({reviewNotes.length})</h3>
            {/* No inner scroller: the Modal body scrolls as one. */}
            <ul className="space-y-2">
              {reviewNotes.map((note, i) => (
                <li
                  key={`${note.line_number ?? 'x'}-${i}`}
                  className="rounded-[var(--r-sm)] border border-[var(--bd)] bg-[var(--bg-2)] px-3 py-2"
                  data-testid="export-dialog-review-item"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    {note.line_number != null && (
                      <span className="font-[var(--font-mono)] text-[11px] text-[var(--tx-lo)]">
                        Line {note.line_number}
                      </span>
                    )}
                    {note.room && (
                      <span className="text-[11px] text-[var(--tx-lo)]">{note.room}</span>
                    )}
                    <span className="text-[13px] text-[var(--tx-hi)]">{note.description}</span>
                    {note.code && (
                      <span className="font-[var(--font-mono)] text-[11px] text-[var(--tx-lo)]">
                        {note.code}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-[var(--tx-lo)] mt-0.5">{note.note}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {pasteCheck && pasteCheck.entries.length > 0 && (
          <section data-testid="export-dialog-paste-check">
            <h3 className={H3}>{pasteCheck.heading}</h3>
            <ul className="space-y-2">
              {pasteCheck.entries.map((en, i) => (
                <li
                  key={`${en.room}-${i}`}
                  className="rounded-[var(--r-sm)] border border-[var(--bd)] bg-[var(--bg-2)] px-3 py-2"
                  data-testid="export-dialog-paste-item"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-[11px] text-[var(--tx-lo)]">{en.room}</span>
                    <span className="text-[13px] text-[var(--tx-hi)]">{en.item}</span>
                    <span className="font-[var(--font-mono)] text-[11px] text-[var(--tx-lo)]">
                      {en.amount}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Modal>
  );
}
